/**
 * LLM Cloud — model catalog, instances, credential keys, workspace default,
 * MyModel detail.
 */

import { ApiError, extractError, get, post, put } from '../_shared.ts';

export interface MyModelCatalogRow {
  id: string;
  model_id: string;
  display_name: string;
  description: string | null;
  provider_slug: string;
  provider_display_name: string | null;
  /** Push #76G — D-150 multi-axis tags surfaced as small pills on the
   *  user-mode Models tiles. Vocabulary: reasoning · fast · image_generation
   *  · code · cost_optimized · regulated. Null when no tags set on the
   *  template; rendered as uppercase pills using the same chrome as the
   *  Library/market ModelTile (Push #75). */
  recommended_for: string[] | null;
  is_installed: boolean;
  is_mine: boolean;
  is_preview: boolean;
  /** Phase 2 Block 2A Cluster B — assigned instance short_id (lmi-prefix)
   *  when is_mine, else null. Drives /models/:short_id deep-link. */
  instance_short_id: string | null;
}

export function fetchMyModelCatalog(): Promise<{ templates: MyModelCatalogRow[] }> {
  return get('/api/me/models/catalog');
}

export interface AdminModelInstanceDetail {
  id: string;
  /** Phase 2 Block 2A — llm__model_instance.short_id (lmi-prefix). */
  short_id: string;
  template_id: string;
  /** Push 4-1af (2026-07-10): llm__model_template.short_id (lmt-prefix) for
   *  the "View in market" action → /admin/market/models/:template_short_id. */
  template_short_id: string;
  template_slug: string;
  provider_instance_id: string;
  provider_slug: string;
  provider_display_name: string | null;
  display_name: string;
  // Push #4.6 / Migration 0117 — per-instance customization overrides.
  description_override: string | null;
  usage_guidance: string | null;
  recommended_for_override: string[] | null;
  recommended_for_template: string[] | null;
  bound_credential_id: string | null;
  bound_credential_label: string | null;
  /** Push 4-1af (2026-07-10): true when the workspace default model template
   *  matches this instance's model_template_id. Drives the conditional
   *  "Set as default" action on AdminModelDetail. */
  is_workspace_default: boolean;
  /** Push 4-1aj (2026-07-10): count of DISTINCT account_users with active
   *  access to this model via direct assignment OR permission-set-mediated
   *  assignment. Powers the "N users lose access" impact bullet on the
   *  Uninstall ConfirmDialog. Wire-shape is string (COUNT bigint); parse
   *  before comparing to numbers. */
  assigned_user_count: string;
  created_at: string;
}
export interface AdminModelAvailableCredential {
  id: string;
  source: string;
  display_label: string | null;
  secret_manager_path: string;
  account_user_id: string | null;
  created_at: string;
}
export interface AdminModelInstancePayload {
  instance: AdminModelInstanceDetail;
  available_credentials: AdminModelAvailableCredential[];
}
/**
 * Phase 2 Block 2A Cluster B (2026-06-30) — URL flattened from
 *   /api/admin/models/instances/:id  →  /api/admin/models/:short_id
 * Caller passes llm__model_instance.short_id (lmi-prefix).
 */
export function fetchAdminModelInstance(short_id: string): Promise<AdminModelInstancePayload> {
  return get(`/api/admin/models/${encodeURIComponent(short_id)}`);
}

export interface AdminModelInstancePatch {
  display_name?: string;
  description_override?: string | null;
  usage_guidance?: string | null;
  recommended_for_override?: string[] | null;
}
/** Phase 2 Block 2A — URL flattened; param is llm__model_instance.short_id. */
export function updateAdminModelInstance(
  short_id: string,
  patch: AdminModelInstancePatch,
): Promise<{ ok: true; changed: number }> {
  return fetch(`/api/admin/models/${encodeURIComponent(short_id)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  }).then(async (res) => {
    const text = await res.text();
    const parsed: unknown = text === '' ? null : (JSON.parse(text) as unknown);
    if (!res.ok) {
      const msg =
        parsed !== null && typeof parsed === 'object' && 'message' in parsed
          ? String((parsed as { message: unknown }).message)
          : `PATCH /api/admin/models/${short_id} failed (${res.status})`;
      throw new ApiError(res.status, msg);
    }
    return parsed as { ok: true; changed: number };
  });
}

// ---- LLM Cloud — model catalog + instances ----
//
// Interfaces aligned to migration 0022 (llm_cloud_schema). DB column names
// preserved: `name` (provider slug), `kind` (provider type), `model_id`
// (model slug), `context_window` (token count), `price_per_input_token`
// (per-token USD), `price_per_output_token`, `security_tier`.
// Per CLAUDE.md TS-types-match-DB discipline.

export interface LlmProviderTemplate {
  id: string;
  /** DB column `name` — slug-style: 'anthropic', 'openai', 'google', 'aws_bedrock'. */
  name: string;
  display_name: string;
  /** DB column `kind` — provider category. Same value as `name` at v1; future tiering. */
  kind: string;
  /** Computed from `status = 'coming_soon'` (per D-156 migration 0042). UI labels as "coming soon". */
  is_preview: boolean;
  /** Convenience alias of `name` for AdminModels.tsx existing prov.slug lookup. */
  slug: string;
}

export interface LlmModelTemplate {
  id: string;
  provider_template_id: string;
  /** Joined from llm__provider_template.name. */
  provider_slug: string;
  /** DB column `model_id` — vendor-side model identifier (e.g., 'claude-3-5-haiku-20241022'). */
  model_id: string;
  display_name: string;
  description: string | null;
  /** DB column `security_tier` — 'standard' | 'enterprise' | 'restricted'. UI may map to a friendly label. */
  security_tier: string;
  /** DB column `context_window` — max input tokens for this model. */
  context_window: number | null;
  /** DB column `price_per_input_token` — USD per token. Multiply by 1_000_000 for per-million display. */
  price_per_input_token: number | null;
  price_per_output_token: number | null;
  /** Computed from `status = 'coming_soon'` (per D-156 migration 0042). UI labels as "coming soon". */
  is_preview: boolean;
  /** Convenience alias of `model_id`. */
  slug: string;
  /** Computed UI hint derived from security_tier or recommended_for; falls back to security_tier. */
  tier_label: string | null;
}

export interface LlmModelInstance {
  id: string;
  /** Phase 2 Block 2A Cluster B — llm__model_instance.short_id (lmi-prefix).
   *  Used by list pages to navigate to /models/:short_id and /admin/models/:short_id. */
  short_id: string;
  /** DB column `model_template_id`. */
  template_id: string;
  /** Joined from template.model_id. */
  template_slug: string;
  /** Joined from provider_template.name — slug-style, lowercase ('anthropic'). API name; don't show to end users. */
  provider_slug: string;
  /** Joined from provider_template.display_name — title-cased ('Anthropic'). User-facing label. Optional for backward compat with older API responses. */
  provider_display_name?: string;
  display_name: string;
  /**
   * Push 74 (2026-07-18) — end-user "when to use" description. Computed at
   * the /api/me/models endpoint as COALESCE(mi.description_override,
   * mt.description). Admin curates via AdminModelDetail's Model settings
   * card (description_override on llm__model_instance); the shipped default
   * lives on llm__model_template.description. The ModelPickerModal renders
   * this as the second line under the display_name — replaces the previous
   * tier_hint-only line, which was tier-level (couldn't reflect per-model
   * curation). Optional for backward compat with older API responses.
   */
  description?: string | null;
  /** Derived at the API layer from sys__account.settings->>'default_model_instance_id'
   *  (Migration E close 2026-06-23 — sys__preference table dropped; default lives in
   *  sys__account.settings JSONB blob). */
  is_workspace_default: boolean;
  /** Joined from llm__model_assignment.credential_instance_id (any active assignment); null if not yet bound. */
  credential_instance_id: string | null;
  /** D-150: effective recommended_for tags (instance override || template default).
   *  Vocabulary: reasoning, fast, image_generation, code, cost_optimized, regulated.
   *  Optional for backward compat with older API responses. */
  recommended_for?: string[];
  /** Push #78 round 26 — capability tokens from the catalog template
   *  (provider-agnostic feature flags: function_calling / vision /
   *  web_search / documents / streaming / json_mode). UI consumes via
   *  modelSupports(model, capability) from @olytic/shared. Optional for
   *  backward compat with older API responses; defaults to []. */
  capabilities?: string[];
  /** Push 5-D Item 5 (2026-07-11) — max input tokens for this model,
   *  from llm__model_template.context_window. The chat composer uses
   *  this to render a soft warning (>80% full) + hard block (>95%) on
   *  the pending user message. Null when unset in the catalog; the UI
   *  falls back to no warning in that case (safe — no false positives). */
  context_window?: number | null;
  /** Wave B / B6: tier classification derived from template_slug.
   *  'fast' (Haiku) | 'smart' (Sonnet) | 'powerful' (Opus) | 'other' (non-Claude). */
  tier?: 'fast' | 'smart' | 'powerful' | 'other';
  /** Wave B / B6: human-readable tier label ('Fast' | 'Smart' | 'Powerful'). null when tier='other'. */
  tier_label?: string | null;
  /** Wave B / B6: one-line trade-off shown next to the tier label. null when tier='other'. */
  tier_hint?: string | null;
  /** Wave B / B6: relative price chip ('$' | '$$' | '$$$'). null when tier='other'. */
  price_band?: '$' | '$$' | '$$$' | null;
  created_at: string;
}

export function fetchModelCatalog(): Promise<{
  providers: LlmProviderTemplate[];
  models: LlmModelTemplate[];
}> {
  return get('/api/admin/models/catalog');
}

export function fetchModelInstances(): Promise<{ instances: LlmModelInstance[] }> {
  return get('/api/admin/models/instances');
}

export function cloneModelTemplate(modelTemplateId: string): Promise<{
  ok: true;
  instance_id: string;
}> {
  return post('/api/admin/models/instances', { template_id: modelTemplateId });
}

/**
 * Phase 1.31 (2026-06-29): soft-delete a model instance. Used by the
 * welcome wizard's Finish-time uninstall fan-out when a user marked an
 * already-installed model for removal. Refuses (409 is_default_model)
 * if the target is the workspace default — caller must switch default
 * first via setAdminWorkspaceDefaultModel.
 */
/**
 * Phase 2 Block 2A Cluster B — caller-side: pass llm__model_instance.short_id
 * (lmi-prefix). URL flattened from /instances/:id to /:short_id.
 */
export async function deleteModelInstance(short_id: string): Promise<{ ok: true }> {
  const path = `/api/admin/models/${encodeURIComponent(short_id)}`;
  const res = await fetch(path, { method: 'DELETE', credentials: 'include' });
  if (!res.ok) {
    const text = await res.text();
    const errInfo = extractError(text, path, 'DELETE', res.status);
    throw new ApiError(res.status, errInfo.message, errInfo.detail, errInfo.incidentId, errInfo.code);
  }
  return (await res.json()) as { ok: true };
}

/**
 * Push 4-1g (2026-07-08): Clone a model instance. Deep-copies the source
 * row under a new short_id + display_name; fresh clones start unassigned
 * (fresh assignment via the standard PS workshop after landing on the
 * new record). Endpoint returns 501 until the clone machinery lands.
 */
export async function cloneAdminModelInstance(
  short_id: string,
): Promise<{ ok: true; short_id: string }> {
  const path = `/api/admin/models/${encodeURIComponent(short_id)}/clone`;
  const res = await fetch(path, { method: 'POST', credentials: 'include' });
  const text = await res.text();
  if (!res.ok) {
    const errInfo = extractError(text, path, 'POST', res.status);
    throw new ApiError(res.status, errInfo.message, errInfo.detail, errInfo.incidentId, errInfo.code);
  }
  return JSON.parse(text) as { ok: true; short_id: string };
}

/** Phase 2 Block 2A — caller passes llm__model_instance.short_id. */
export function setWorkspaceDefaultModel(short_id: string): Promise<{ ok: true }> {
  return post(`/api/admin/models/${encodeURIComponent(short_id)}/default`, {});
}

/**
 * Push 4-1l (2026-07-09) — power-admin per-account override for a model
 * instance. Passes an optional subset of override columns; empty body ⇒ 400.
 */
export interface ModelOverrideBody {
  display_name?: string;
  description_override?: string | null;
  usage_guidance?: string | null;
  recommended_for_override?: string[] | null;
}
export function overrideAdminModelInstance(
  short_id: string,
  body: ModelOverrideBody,
): Promise<{ ok: true; instance_id: string }> {
  return post(
    `/api/admin/models/${encodeURIComponent(short_id)}/override`,
    body,
  );
}

/** User-side: which models am I assigned to use (default + alternatives).
 *  Wave B / B6: API now also returns workspace_default_template_id so the
 *  SPA can render the "workspace default" pill on the tier matching the
 *  admin's pin, not on whichever instance happens to be alphabetically first. */
export function fetchMyModels(): Promise<{
  default: LlmModelInstance | null;
  alternatives: LlmModelInstance[];
  workspace_default_template_id: string | null;
}> {
  return get('/api/me/models');
}

/**
 * Push 5-C-cont (2026-07-11) — per-model average credits per turn over
 * the caller's last 14 days. Powers the ModelPickerModal's per-row
 * "~N credits per turn" caption. Grouped by model_template_id so the
 * caller can join against LlmModelInstance.template_id at render time.
 */
export interface ModelBurnHint {
  model_template_id: string;
  avg_credits_per_turn: number;
  turn_count: number;
}
export function fetchModelBurnHints(): Promise<{ hints: ModelBurnHint[] }> {
  return get('/api/me/models/burn-hints');
}

/** Wave B / B7: workspace default resolved for the current user.
 *  The chat composer hits this on mount so a brand-new session knows which
 *  model to preselect before the user types. Returns null template_id when
 *  the workspace admin hasn't pinned a default. */
export function fetchMyWorkspaceDefaultModel(): Promise<{
  template_id: string | null;
  template_slug: string | null;
}> {
  return get('/api/me/workspace/default-model');
}

/** Wave B / B7: admin getter for the workspace default model template. */
export function fetchAdminWorkspaceDefaultModel(): Promise<{
  template_id: string | null;
  template_slug: string | null;
  display_name: string | null;
}> {
  return get('/api/admin/workspace/default-model');
}

/** Wave B / B7: admin setter for the workspace default model template.
 *  Pass templateId=null to clear the default. */
export function setAdminWorkspaceDefaultModel(
  templateId: string | null,
): Promise<{ ok: true; template_id: string | null }> {
  return put('/api/admin/workspace/default-model', { template_id: templateId });
}

export interface ModelPermissionSetMembership {
  permission_set_id: string;
  permission_set_name: string;
  permission_set_display_name: string;
  kind: string;
}

export interface MyModelDetail {
  instance: {
    id: string;
    /** Phase 2 Block 2A — llm__model_instance.short_id (lmi-prefix) */
    short_id: string;
    template_id: string;
    /** Push 4-1af (2026-07-10): llm__model_template.short_id (lmt-prefix) for
     *  the "View in market" action → /market/models/:template_short_id. */
    template_short_id: string;
    template_slug: string;
    provider_slug: string;
    provider_display_name: string;
    display_name: string;
    description: string | null;
    context_window: number | null;
    security_tier: string;
    price_per_input_token: number | null;
    price_per_output_token: number | null;
    credential_instance_id: string;
    credential_display_label: string;
    credential_source: string;
    credential_secret_manager_path: string;
    created_at: string;
    is_workspace_default: boolean;
  };
  permission_sets: ModelPermissionSetMembership[];
  recent_usage: Array<unknown>;
}

/**
 * Phase 2 Block 2A Cluster B — caller-side: pass llm__model_instance.short_id
 * (lmi-prefix). Server WHERE mi.short_id = $3.
 */
export function fetchMyModelDetail(short_id: string): Promise<MyModelDetail> {
  return get(`/api/me/models/${encodeURIComponent(short_id)}`);
}

// ---- LLM Cloud — credential instances ----

export interface LlmKeyInstance {
  id: string;
  provider_instance_id: string;
  provider_slug: string;
  /** 'olytic_managed' (default — Olytic supplies the API key; path:
   *    `llm-olytic-managed-{provider_slug}` per D-137)
   *  | 'byo_account' (BYO key scoped to the whole account; path:
   *    `llm-byo-{provider_slug}-account-{account_id}-user-{user_id}` per D-137)
   *  | 'byo_user' (BYO key scoped to a single user; same path shape as
   *    byo_account, scope-distinguished by the row's account_user_id binding). */
  source: 'olytic_managed' | 'byo_account' | 'byo_user';
  /** Display label only — the actual key value lives in Secret Manager per D-130. */
  label: string;
  /** Canonical Secret Manager path per D-137 (lowercase, hyphen-separated, provider
   *  slug matches `llm__provider_template.slug`). Path only — never the value. */
  secret_manager_path: string;
  created_at: string;
}

export function fetchKeyInstances(): Promise<{ credentials: LlmKeyInstance[] }> {
  return get('/api/admin/credentials');
}

/**
 * Advanced opt-in per Discipline #7: workspaces ship with Olytic-managed
 * credentials by default. BYO is power-user-only — the SPA renders the toggle
 * behind an "Advanced settings" disclosure on /admin/credentials.
 */
export function startByoCredentialFlow(payload: {
  provider_instance_id: string;
  source: 'byo_account' | 'byo_user';
  label: string;
}): Promise<{ ok: true; upload_url: string; credential_instance_id: string }> {
  return post('/api/admin/credentials/byo', payload);
}
